import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ForecastData {
  month: string;
  actual: number;
  predicted: number;
}

const mockData: ForecastData[] = [
  { month: 'Jan', actual: 5000, predicted: 5200 },
  { month: 'Feb', actual: 5500, predicted: 5700 },
  { month: 'Mar', actual: 6000, predicted: 6300 },
  { month: 'Apr', actual: 0, predicted: 6800 },
  { month: 'May', actual: 0, predicted: 7200 },
  { month: 'Jun', actual: 0, predicted: 7500 },
];

export function FinancialForecasting() {
  const [forecastData, setForecastData] = useState<ForecastData[]>(mockData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchForecasts() {
      try {
        const { data, error } = await supabase
          .from('financial_forecasts')
          .select('forecast_data')
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) throw error;

        // If we have data, use it; otherwise, keep using mock data
        if (data && data.length > 0) {
          setForecastData(data[0].forecast_data);
        }
      } catch (error) {
        console.error('Error fetching forecasts:', error);
        setError('Failed to fetch forecast data');
        // Keep using mock data on error
      } finally {
        setLoading(false);
      }
    }

    fetchForecasts();
  }, []);

  return (
    <div className="bg-dark-card rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <TrendingUp className="h-6 w-6 text-primary-400" />
          <h2 className="text-xl font-semibold text-dark-text">Financial Forecast</h2>
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400" />
        </div>
      ) : error ? (
        <div className="h-64 flex items-center justify-center text-dark-muted">
          <AlertCircle className="h-6 w-6 mr-2" />
          <span>{error}</span>
        </div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={forecastData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9CA3AF" />
              <YAxis stroke="#9CA3AF" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '0.5rem',
                }}
              />
              <Line
                type="monotone"
                dataKey="actual"
                name="Actual Revenue"
                stroke="#0EA5E9"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="predicted"
                name="Predicted Revenue"
                stroke="#22C55E"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-4 flex items-center justify-center space-x-6">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-[#0EA5E9] rounded-full mr-2" />
          <span className="text-sm text-dark-muted">Actual Revenue</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-[#22C55E] rounded-full mr-2" />
          <span className="text-sm text-dark-muted">Predicted Revenue</span>
        </div>
      </div>
    </div>
  );
}
